import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { PiggyBank, Fuel, ArrowRight } from "lucide-react";

// Prix moyens constatés au 22/09/2026
const PRIX = { sp95: 2.17, gazole: 2.4, e85: 0.88 };
const OVERCONSO_E85 = 1.25; // surconsommation habituelle incluse

const fmt = new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });

const COLORS = { sp95: "#6EB000", gazole: "#F5A623", e85: "#4DA3FF" };

export default function SavingsCalculator() {
  const [km, setKm] = useState(20000);
  const [conso, setConso] = useState(7);

  const { sp95, gazole, e85, maxSave } = useMemo(() => {
    const litres = (km / 100) * conso;
    const sp95 = litres * PRIX.sp95;
    const gazole = litres * PRIX.gazole;
    const e85 = litres * OVERCONSO_E85 * PRIX.e85;
    return { sp95, gazole, e85, maxSave: Math.max(sp95, gazole) - e85 };
  }, [km, conso]);

  const blocks = [
    { id: "sp95", label: "SP95", price: "2,17 €/L", value: sp95, color: COLORS.sp95 },
    { id: "gazole", label: "Gazole", price: "2,40 €/L", value: gazole, color: COLORS.gazole },
    { id: "e85", label: "E85", price: "0,88 €/L", value: e85, color: COLORS.e85 },
  ];

  return (
    <section className="relative py-24 sm:py-32 border-t border-white/10 bg-[#2E3336]" data-testid="savings-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
        >
          <p className="font-script text-2xl text-[#6EB000] mb-2">Faites le calcul vous-même</p>
          <h2 className="font-display text-4xl sm:text-6xl text-white leading-[0.95]" data-testid="comparator-title">
            <span style={{ color: COLORS.sp95 }}>SP95</span>
            <span className="text-white/25"> · </span>
            <span style={{ color: COLORS.gazole }}>gazole</span>
            <span className="text-white/25"> · </span>
            <span style={{ color: COLORS.e85 }}>E85</span>
          </h2>
          <p className="text-white/60 mt-6 max-w-md text-sm sm:text-base leading-relaxed">
            Comparez le budget annuel de chaque carburant selon votre kilométrage et votre consommation.
            L'estimation E85 inclut la surconsommation habituelle (+25&nbsp;%).
          </p>

          <div className="mt-10 space-y-8">
            <div>
              <div className="flex justify-between text-sm mb-3">
                <label htmlFor="km-slider" className="uppercase tracking-widest text-white/70">Kilométrage annuel</label>
                <span className="font-display text-[#6EB000]" data-testid="km-value">{km.toLocaleString("fr-FR")} km</span>
              </div>
              <input
                id="km-slider"
                type="range"
                min="5000"
                max="50000"
                step="1000"
                value={km}
                onChange={(e) => setKm(Number(e.target.value))}
                className="w-full"
                data-testid="km-slider"
              />
            </div>
            <div>
              <div className="flex justify-between text-sm mb-3">
                <label htmlFor="conso-slider" className="uppercase tracking-widest text-white/70">Consommation moyenne</label>
                <span className="font-display text-[#6EB000]" data-testid="conso-value">{conso.toFixed(1)} L/100</span>
              </div>
              <input
                id="conso-slider"
                type="range"
                min="4"
                max="12"
                step="0.5"
                value={conso}
                onChange={(e) => setConso(Number(e.target.value))}
                className="w-full"
                data-testid="conso-slider"
              />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="space-y-5"
          data-testid="savings-result-card"
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5">
            {blocks.map((b) => (
              <div
                key={b.id}
                className="relative flex flex-col items-center bg-[#343A3D] border rounded-xl px-5 py-7 text-center overflow-hidden"
                style={{ borderColor: `${b.color}40` }}
                data-testid={`budget-${b.id}`}
              >
                <div
                  className="absolute top-0 left-0 right-0 h-[3px]"
                  style={{ background: `linear-gradient(to right, transparent, ${b.color}, transparent)` }}
                />
                <Fuel className="w-6 h-6" style={{ color: b.color }} strokeWidth={1.6} />
                <p className="font-display text-lg text-white mt-4 leading-none">{b.label}</p>
                <p className="text-[11px] uppercase tracking-[0.2em] mt-2" style={{ color: b.color }}>{b.price}</p>
                <div className="w-8 h-px my-5" style={{ backgroundColor: `${b.color}55` }} />
                <p className="text-[11px] uppercase tracking-[0.2em] text-white/40">Budget / an</p>
                <p
                  className="font-display text-2xl sm:text-3xl mt-2 leading-none"
                  style={{ color: b.color }}
                  data-testid={`budget-value-${b.id}`}
                >
                  {fmt.format(b.value)}
                </p>
              </div>
            ))}
          </div>

          <div
            className="glow-green bg-[#343A3D] border border-[#6EB000]/40 rounded-xl px-6 py-10 sm:px-10 text-center"
            data-testid="savings-max-block"
          >
            <PiggyBank className="w-9 h-9 text-[#6EB000] mx-auto" strokeWidth={1.5} />
            <p className="uppercase tracking-[0.2em] text-white/55 text-xs mt-5 max-w-xs mx-auto leading-relaxed">
              Économie maximale par an en passant à l'E85
            </p>
            <p className="font-display text-5xl sm:text-7xl text-[#6EB000] mt-4 leading-none" data-testid="savings-max">
              {fmt.format(maxSave)}
            </p>
            <div className="w-10 h-px bg-[#6EB000]/40 mx-auto mt-7" />
            <p className="font-script text-xl text-[#4DA3FF] mt-6" data-testid="savings-note">
              * Estimation indicative — selon compatibilité du véhicule
            </p>
          </div>

          <p className="text-center text-[11px] text-white/35 pt-1" data-testid="prices-note">
            Prix moyens constatés au 22/09/2026
          </p>

          <div className="pt-3 text-center">
            <Link
              to="/configurateur?prestation=e85-carto"
              className="clip-angle inline-flex items-center gap-2 bg-[#6EB000] text-black font-bold px-8 py-4 text-sm uppercase tracking-widest hover:bg-[#5A9000] active:scale-95 transition-all duration-200"
              data-testid="convert-e85-cta"
            >
              Convertir avec NBPerf
              <ArrowRight className="w-4 h-4" strokeWidth={2.2} />
            </Link>
            <p className="text-white/40 text-xs mt-3">Devis E85 pré-rempli — sans engagement</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
